import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import { API_LINK } from '../../apiLink/apiLink';

const AccountsReports = ({ onNavigate, userInfo = {} }) => {
    // Accounts User Data State
    const [accountsData, setAccountsData] = useState({
        name: "Loading...",
        accountsId: "...",
        department: "Accounts Department",
        designation: "Accounts Officer",
        profileImage: null
    });

    const [activeTab, setActiveTab] = useState('gst-ledger');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [reportData, setReportData] = useState(null);

    // Filters
    const [dateRange, setDateRange] = useState({
        startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
        endDate: new Date().toISOString().split('T')[0]
    });
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

    // Fetch User Info
    useEffect(() => {
        setAccountsData({
            name: userInfo?.name || "Accounts User",
            accountsId: userInfo?.id || "ACC2024001",
            department: "Accounts Department",
            designation: "Accounts Officer",
            profileImage: null
        });
    }, [userInfo]);

    // Fetch Report Data
    const fetchReportData = async () => {
        try {
            setLoading(true);
            setError(null);
            const token = localStorage.getItem('token') || sessionStorage.getItem('token');
            if (!token) throw new Error("Authentication token not found");

            let endpoint = '';
            let params = new URLSearchParams();

            if (activeTab === 'gst-ledger') {
                endpoint = '/api/accounts/reports/gst-ledger';
                if (dateRange.startDate) params.append('startDate', dateRange.startDate);
                if (dateRange.endDate) params.append('endDate', dateRange.endDate);
            } else if (activeTab === 'balance-sheet') {
                endpoint = '/api/accounts/reports/balance-sheet';
            } else if (activeTab === 'income-statement') {
                endpoint = '/api/accounts/reports/income-statement';
                params.append('year', selectedYear);
            } else if (activeTab === 'cash-flow') {
                endpoint = '/api/accounts/reports/cash-flow';
                params.append('year', selectedYear);
            }

            const response = await fetch(`${API_LINK}${endpoint}?${params.toString()}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (!response.ok) throw new Error("Failed to fetch report data");
            const result = await response.json();

            if (result.success) {
                setReportData(result.data);
            } else {
                setError(result.message || "Failed to load report");
            }

        } catch (err) {
            console.error(err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchReportData();
    }, [activeTab, dateRange, selectedYear]);

    // Render GST Ledger Table
    const renderGSTLedger = () => {
        if (!reportData || reportData.length === 0) return <div style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>No GST transactions found for this period.</div>;

        return (
            <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '1rem' }}>
                    <thead>
                        <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                            <th style={{ padding: '1rem', textAlign: 'left', color: '#475569' }}>Date</th>
                            <th style={{ padding: '1rem', textAlign: 'left', color: '#475569' }}>Ref No</th>
                            <th style={{ padding: '1rem', textAlign: 'left', color: '#475569' }}>Party Name</th>
                            <th style={{ padding: '1rem', textAlign: 'left', color: '#475569' }}>Type</th>
                            <th style={{ padding: '1rem', textAlign: 'right', color: '#475569' }}>Taxable Value</th>
                            <th style={{ padding: '1rem', textAlign: 'right', color: '#475569' }}>Tax Amount</th>
                            <th style={{ padding: '1rem', textAlign: 'right', color: '#475569' }}>Total</th>
                        </tr>
                    </thead>
                    <tbody>
                        {reportData.map((row, index) => (
                            <tr key={index} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                <td style={{ padding: '1rem' }}>{new Date(row.date).toLocaleDateString()}</td>
                                <td style={{ padding: '1rem' }}>{row.refNo}</td>
                                <td style={{ padding: '1rem' }}>{row.partyName}</td>
                                <td style={{ padding: '1rem' }}>{row.type}</td>
                                <td style={{ padding: '1rem', textAlign: 'right' }}>₹{parseFloat(row.taxableValue).toFixed(2)}</td>
                                <td style={{ padding: '1rem', textAlign: 'right', color: '#dc2626' }}>₹{parseFloat(row.taxAmount).toFixed(2)}</td>
                                <td style={{ padding: '1rem', textAlign: 'right', fontWeight: 'bold' }}>₹{parseFloat(row.totalAmount).toFixed(2)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    };

    // Render Balance Sheet
    const renderBalanceSheet = () => {
        if (!reportData) return null;
        const { assets, liabilities, equity } = reportData;

        // Safety check to ensure data structure is correct
        if (!assets || !liabilities || !equity) return null;

        const Section = ({ title, data, total, color }) => (
            <div style={{ flex: 1, background: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '1.5rem' }}>
                <h3 style={{ borderBottom: `3px solid ${color}`, paddingBottom: '0.5rem', marginBottom: '1rem', color: '#1e293b' }}>{title}</h3>
                {Object.entries(data).map(([key, value]) => (
                    key !== 'total' && (
                        <div key={key} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.75rem', fontSize: '0.95rem' }}>
                            <span style={{ textTransform: 'capitalize', color: '#64748b' }}>{key.replace(/([A-Z])/g, ' $1').trim()}</span>
                            <span style={{ fontWeight: 500 }}>₹{parseFloat(value).toLocaleString()}</span>
                        </div>
                    )
                ))}
                <div style={{ marginTop: '1.5rem', paddingTop: '1rem', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', fontWeight: 'bold', fontSize: '1.1rem' }}>
                    <span>Total {title}</span>
                    <span style={{ color: color }}>₹{parseFloat(total).toLocaleString()}</span>
                </div>
            </div>
        );

        return (
            <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap' }}>
                <Section title="Assets" data={assets} total={assets.total} color="#22c55e" />
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem', flex: 1 }}>
                    <Section title="Liabilities" data={liabilities} total={liabilities.total} color="#ef4444" />
                    <Section title="Equity" data={equity} total={equity.total} color="#3b82f6" />
                </div>
            </div>
        );
    };

    // Render Income Statement & Cash Flow (Shared Structure)
    const renderMonthlyStatement = (isCashFlow = false) => {
        if (!reportData) return null;

        const columns = isCashFlow
            ? { incoming: 'Inflow', outgoing: 'Outflow', net: 'Net Cash' }
            : { incoming: 'Revenue', outgoing: 'Expenses', net: 'Net Income' };

        return (
            <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '1rem' }}>
                    <thead>
                        <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                            <th style={{ padding: '1rem', textAlign: 'left', color: '#475569' }}>Month</th>
                            <th style={{ padding: '1rem', textAlign: 'right', color: '#22c55e' }}>{columns.incoming}</th>
                            <th style={{ padding: '1rem', textAlign: 'right', color: '#ef4444' }}>{columns.outgoing}</th>
                            <th style={{ padding: '1rem', textAlign: 'right', color: '#475569' }}>{columns.net}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {reportData.map((row, index) => {
                            const incoming = isCashFlow ? row.inflow : row.revenue;
                            const outgoing = isCashFlow ? row.outflow : row.expenses;
                            const net = isCashFlow ? row.netCash : row.netIncome;

                            return (
                                <tr key={index} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                    <td style={{ padding: '1rem', fontWeight: 500 }}>{row.month}</td>
                                    <td style={{ padding: '1rem', textAlign: 'right', color: '#166534' }}>
                                        {incoming > 0 ? `₹${parseFloat(incoming).toLocaleString()}` : '-'}
                                    </td>
                                    <td style={{ padding: '1rem', textAlign: 'right', color: '#991b1b' }}>
                                        {outgoing > 0 ? `₹${parseFloat(outgoing).toLocaleString()}` : '-'}
                                    </td>
                                    <td style={{ padding: '1rem', textAlign: 'right', fontWeight: 'bold' }}>
                                        <span style={{
                                            background: net >= 0 ? '#dcfce7' : '#fee2e2',
                                            color: net >= 0 ? '#166534' : '#991b1b',
                                            padding: '0.25rem 0.75rem',
                                            borderRadius: '999px',
                                            fontSize: '0.9rem'
                                        }}>
                                            {net < 0 ? '-' : ''}₹{Math.abs(net).toLocaleString()}
                                        </span>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        );
    };

    return (
        <Layout role="accounts"
            onNavigate={onNavigate}
            activePage="accounts-reports"
            pageTitle="Financial Reports"
            accountsData={accountsData}
        >
            {/* Header */}
            <div style={{
                background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
                color: '#ffffff',
                padding: '2rem',
                borderRadius: '12px',
                marginBottom: '2rem',
                boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
            }}>
                <h2 style={{ margin: '0 0 0.5rem 0', fontSize: '1.5rem' }}>Financial Reports 📊</h2>
                <p style={{ margin: 0, opacity: 0.8 }}>View comprehensive financial statements and transaction ledgers.</p>
            </div>

            {/* Controls & Tabs */}
            <div style={{ background: '#fff', borderRadius: '12px', padding: '1rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginBottom: '2rem' }}>
                <div style={{ display: 'flex', gap: '1rem', borderBottom: '1px solid #e2e8f0', paddingBottom: '1rem', marginBottom: '1.5rem', overflowX: 'auto' }}>
                    {[
                        { id: 'gst-ledger', label: 'GST Ledger' },
                        { id: 'balance-sheet', label: 'Balance Sheet' },
                        { id: 'income-statement', label: 'Income Statement' },
                        { id: 'cash-flow', label: 'Cash Flow' }
                    ].map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => {
                                setReportData(null);
                                setActiveTab(tab.id);
                            }}
                            style={{
                                padding: '0.75rem 1.5rem',
                                background: activeTab === tab.id ? '#eff6ff' : 'transparent',
                                color: activeTab === tab.id ? '#2563eb' : '#64748b',
                                border: 'none',
                                borderRadius: '8px',
                                fontWeight: 600,
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>

                {/* Filters */}
                <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                    {activeTab === 'gst-ledger' && (
                        <>
                            <div>
                                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, color: '#64748b', marginBottom: '0.5rem' }}>Start Date</label>
                                <input
                                    type="date"
                                    value={dateRange.startDate}
                                    onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value }))}
                                    style={{ padding: '0.6rem', borderRadius: '6px', border: '1px solid #cbd5e1', width: '160px' }}
                                />
                            </div>
                            <div>
                                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, color: '#64748b', marginBottom: '0.5rem' }}>End Date</label>
                                <input
                                    type="date"
                                    value={dateRange.endDate}
                                    onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value }))}
                                    style={{ padding: '0.6rem', borderRadius: '6px', border: '1px solid #cbd5e1', width: '160px' }}
                                />
                            </div>
                        </>
                    )}

                    {(activeTab === 'income-statement' || activeTab === 'cash-flow') && (
                        <div>
                            <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, color: '#64748b', marginBottom: '0.5rem' }}>Financial Year</label>
                            <select
                                value={selectedYear}
                                onChange={(e) => setSelectedYear(e.target.value)}
                                style={{ padding: '0.6rem', borderRadius: '6px', border: '1px solid #cbd5e1', width: '120px' }}
                            >
                                <option value="2024">2024</option>
                                <option value="2025">2025</option>
                                <option value="2026">2026</option>
                            </select>
                        </div>
                    )}

                    <div style={{ marginLeft: 'auto' }}>
                        <button
                            onClick={fetchReportData}
                            style={{
                                padding: '0.65rem 1.25rem',
                                background: '#334155',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontWeight: 500,
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem'
                            }}
                        >
                            <span>↻</span> Refresh
                        </button>
                    </div>
                </div>
            </div>

            {/* Content Area */}
            <div style={{ background: '#fff', borderRadius: '12px', padding: '2rem', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}>
                {loading ? (
                    <div style={{ padding: '3rem', textAlign: 'center', color: '#64748b' }}>
                        Loading report data...
                    </div>
                ) : error ? (
                    <div style={{ padding: '2rem', background: '#fef2f2', color: '#dc2626', borderRadius: '8px', border: '1px solid #fecaca' }}>
                        <strong>Error:</strong> {error}
                    </div>
                ) : (
                    <>
                        {activeTab === 'gst-ledger' && renderGSTLedger()}
                        {activeTab === 'balance-sheet' && renderBalanceSheet()}
                        {activeTab === 'income-statement' && renderMonthlyStatement(false)}
                        {activeTab === 'cash-flow' && renderMonthlyStatement(true)}
                    </>
                )}
            </div>

        </Layout>
    );
};

export default AccountsReports;
